import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  Archive,
  Edit,
  Megaphone,
  Plus,
  Power,
  Search,
  Trash2,
} from 'lucide-react';
import {
  useAdminAnnouncements,
  useArchiveAnnouncement,
  useCreateAnnouncement,
  useDeleteAnnouncement,
  useToggleAnnouncement,
  useUpdateAnnouncement,
} from '../hooks/api';
import FormDrawer, { FormSection } from './FormDrawer';
import ConfirmModal from './ConfirmModal';
import { Button, EmptyState, Panel, StatusPill, ViewTabs } from './ui';

const FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'archived', label: 'Archived' },
  { id: 'expired', label: 'Expired' },
];

const AUDIENCE_OPTIONS = [
  { value: 'everyone', label: 'Everyone' },
  { value: 'member', label: 'Members only' },
  { value: 'project_lead', label: 'Project Leads only' },
  { value: 'admin', label: 'Admins only' },
];

const emptyForm = {
  title: '',
  message: '',
  priority: 'medium',
  target_audience: ['everyone'],
  expires_at: '',
  pinned: false,
  active: true,
};

const AnnouncementManager = () => {
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const filters = useMemo(
    () => ({
      status: status === 'all' ? '' : status,
      search,
      page,
      page_size: 20,
    }),
    [status, search, page]
  );

  const { data, isLoading } = useAdminAnnouncements(filters);
  const createMutation = useCreateAnnouncement();
  const updateMutation = useUpdateAnnouncement();
  const deleteMutation = useDeleteAnnouncement();
  const toggleMutation = useToggleAnnouncement();
  const archiveMutation = useArchiveAnnouncement();

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDrawerOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || '',
      message: item.message || '',
      priority: item.priority || 'medium',
      target_audience: item.target_audience?.length ? item.target_audience : ['everyone'],
      expires_at: item.expires_at ? format(new Date(item.expires_at), 'yyyy-MM-dd') : '',
      pinned: !!item.pinned,
      active: !!item.active,
    });
    setDrawerOpen(true);
  };

  const handleAudienceChange = (value) => {
    if (value === 'everyone') {
      setForm((f) => ({ ...f, target_audience: ['everyone'] }));
      return;
    }
    setForm((f) => {
      const withoutEveryone = (f.target_audience || []).filter((a) => a !== 'everyone');
      const exists = withoutEveryone.includes(value);
      const next = exists
        ? withoutEveryone.filter((a) => a !== value)
        : [...withoutEveryone, value];
      return { ...f, target_audience: next.length ? next : ['everyone'] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    const payload = {
      title: form.title.trim(),
      message: form.message.trim(),
      priority: form.priority,
      target_audience: form.target_audience,
      pinned: form.pinned,
      active: form.active,
      expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
    };
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, ...payload });
        toast.success('Announcement updated');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Announcement created');
      }
      setDrawerOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save announcement');
    }
  };

  const statusPill = (item) => {
    if (item.is_archived) return { status: 'Unknown', label: 'Archived' };
    if (item.is_expired) return { status: 'At Risk', label: 'Expired' };
    if (!item.active) return { status: 'On Hold', label: 'Inactive' };
    return { status: 'Completed', label: 'Active' };
  };

  return (
    <div className="space-y-5" data-testid="announcement-manager">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-primary-600" />
            Announcements
          </h2>
          <p className="text-sm text-secondary-500 mt-1">
            Create and manage organization-wide announcements
          </p>
        </div>
        <Button variant="primary" onClick={openCreate} data-testid="create-announcement-btn">
          <Plus className="h-4 w-4" />
          New Announcement
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <ViewTabs
          tabs={FILTER_TABS}
          activeId={status}
          onChange={(id) => {
            setStatus(id);
            setPage(1);
          }}
        />
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
          <input
            type="search"
            className="input-field pl-9 text-sm"
            placeholder="Search title..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <Panel className="overflow-x-auto p-0">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-secondary-500">Loading...</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title="No announcements"
            description="Create your first announcement to notify the team."
            actionLabel="New Announcement"
            onAction={openCreate}
          />
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-secondary-50 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300">
              <tr>
                <th className="text-left font-medium px-4 py-3">Title</th>
                <th className="text-left font-medium px-4 py-3">Priority</th>
                <th className="text-left font-medium px-4 py-3">Audience</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3">Created</th>
                <th className="text-left font-medium px-4 py-3">Expiry</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700">
              {items.map((item) => {
                const pill = statusPill(item);
                return (
                  <tr key={item.id} className="hover:bg-secondary-50/80 dark:hover:bg-secondary-800/40">
                    <td className="px-4 py-3">
                      <div className="font-medium text-secondary-900 dark:text-white">{item.title}</div>
                      {item.pinned && (
                        <span className="text-[11px] text-primary-600">Pinned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 capitalize">{item.priority}</td>
                    <td className="px-4 py-3">
                      {(item.target_audience || []).join(', ')}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={pill.status} label={pill.label} showDot />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {item.created_at ? format(new Date(item.created_at), 'MMM d, yyyy') : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {item.expires_at ? format(new Date(item.expires_at), 'MMM d, yyyy') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          className="p-1.5 rounded hover:bg-secondary-100 dark:hover:bg-secondary-700"
                          title="Edit"
                          onClick={() => openEdit(item)}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="p-1.5 rounded hover:bg-secondary-100 dark:hover:bg-secondary-700"
                          title={item.active ? 'Disable' : 'Enable'}
                          onClick={() =>
                            toggleMutation.mutate(
                              { id: item.id, active: !item.active },
                              {
                                onSuccess: () =>
                                  toast.success(item.active ? 'Disabled' : 'Enabled'),
                                onError: () => toast.error('Failed to toggle'),
                              }
                            )
                          }
                        >
                          <Power className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="p-1.5 rounded hover:bg-secondary-100 dark:hover:bg-secondary-700"
                          title="Archive"
                          onClick={() =>
                            archiveMutation.mutate(item.id, {
                              onSuccess: () => toast.success('Archived'),
                              onError: () => toast.error('Failed to archive'),
                            })
                          }
                        >
                          <Archive className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                          title="Delete"
                          onClick={() => setDeleteTarget(item)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>

      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-secondary-500">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <FormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editing ? 'Edit Announcement' : 'New Announcement'}
        subtitle="Visible to matching roles until expiry"
        icon={<Megaphone className="h-5 w-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDrawerOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
              data-testid="save-announcement-btn"
            >
              {editing ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="announcement-form">
          <FormSection title="Content">
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              className="input-field"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              data-testid="announcement-title"
              required
            />
            <label className="block text-sm font-medium mb-1 mt-3">Message</label>
            <textarea
              className="input-field min-h-[120px]"
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              data-testid="announcement-message"
              required
            />
          </FormSection>
          <FormSection title="Targeting">
            <label className="block text-sm font-medium mb-1">Priority</label>
            <select
              className="input-field"
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              data-testid="announcement-priority"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <label className="block text-sm font-medium mb-2 mt-3">Target audience</label>
            <div className="space-y-2">
              {AUDIENCE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={(form.target_audience || []).includes(opt.value)}
                    onChange={() => handleAudienceChange(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </FormSection>
          <FormSection title="Schedule & options">
            <label className="block text-sm font-medium mb-1">Expiry date</label>
            <input
              type="date"
              className="input-field"
              value={form.expires_at}
              onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
            />
            <label className="flex items-center gap-2 text-sm mt-3">
              <input
                type="checkbox"
                checked={form.pinned}
                onChange={(e) => setForm((f) => ({ ...f, pinned: e.target.checked }))}
              />
              Pin to top
            </label>
            <label className="flex items-center gap-2 text-sm mt-2">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
              Active
            </label>
          </FormSection>
        </form>
      </FormDrawer>

      <ConfirmModal
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        title="Delete announcement"
        message={`Delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmText="Delete"
        confirmButtonStyle="danger"
        isLoading={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate(deleteTarget.id, {
            onSuccess: () => {
              toast.success('Deleted');
              setDeleteTarget(null);
            },
            onError: () => toast.error('Failed to delete'),
          });
        }}
      />
    </div>
  );
};

export default AnnouncementManager;
